import type { Brand } from "@staffsmith/api-types";
import { importPage, PRODUCT_NAME } from "@staffsmith/web-auth";
import { useQuery } from "@tanstack/react-query";
import { Navigate, Outlet, type RouteObject, useNavigate } from "react-router";
import { useServices } from "./context.js";
import { NarrowMenu, NavEntries } from "./Nav.js";
import type { NavEntry } from "./nav-model.js";
import { SignInPage } from "./pages/SignIn.js";

// Each page loads only when first visited (ADR 0003: route-level code splitting).
// A page whose code went with an old deploy reloads the tab or says a new version is out (C1).
const page = (load: () => Promise<{ default: React.ComponentType }>) => async () => ({
  Component: (await importPage(load))?.default ?? (() => null),
});

// The firm's own mark beside the product name (and beside, never instead of,
// the Demo tag). The logo is drawn only as an image, so nothing in it runs.
function FirmMark({ brand, name }: { brand: Brand; name: string }) {
  return (
    <span className="firm-mark">
      {brand.logo_svg ? (
        <img
          src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(brand.logo_svg)}`}
          alt=""
          height={22}
        />
      ) : null}
      <span>{brand.short_name ?? name}</span>
    </span>
  );
}

function Layout() {
  const { api, auth } = useServices();
  const navigate = useNavigate();
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, enabled: auth.isSignedIn() });
  // Features an operator turned off for this firm leave the menu (Phase 18).
  const features = useQuery({
    queryKey: ["features"],
    queryFn: api.features,
    enabled: auth.isSignedIn(),
  });
  const on = (flag: string) => features.data?.[flag] !== false;
  // The status page (Phase 19): a live incident StaffSmith has told firms about.
  const status = useQuery({
    queryKey: ["platform-status"],
    queryFn: api.platformStatus,
    enabled: auth.isSignedIn(),
    // On load and focus, never on a timer: a timer would keep the database awake.
  });
  // The bar's counts: decisions waiting, conversations waiting for a reply, and
  // duplicate pairs to review. Read on load and focus only, like the rest.
  const pending = useQuery({
    queryKey: ["decisions", "pending"],
    queryFn: () => api.decisions({ state: "pending" }),
    enabled: auth.isSignedIn(),
  });
  const threads = useQuery({
    queryKey: ["threads", "open"],
    queryFn: () => api.threads(),
    enabled: auth.isSignedIn(),
  });
  const pairs = useQuery({
    queryKey: ["duplicates"],
    queryFn: api.duplicates,
    enabled: auth.isSignedIn(),
  });
  const items = useQuery({
    queryKey: ["inbox-items"],
    queryFn: api.inboxItems,
    enabled: auth.isSignedIn(),
  });
  if (!auth.isSignedIn()) return <Navigate to="/sign-in" replace />;
  const brand = me.data?.tenant.brand;
  const roles = me.data?.roles ?? [];
  const admin = roles.includes("admin");
  const entries: NavEntry[] = [
    {
      kind: "link",
      item: { to: "/decisions", label: "Decisions", count: pending.data?.data.length ?? 0 },
    },
    {
      kind: "link",
      item: {
        to: "/inbox",
        label: "Inbox",
        // Conversations waiting for a reply, plus items for this person (C4).
        count:
          (threads.data?.data.filter((t) => t.awaiting_reply).length ?? 0) +
          (items.data?.data.length ?? 0),
      },
    },
    {
      kind: "menu",
      group: {
        id: "recruiting",
        label: "Recruiting",
        items: [
          { to: "/jobs", label: "Jobs", description: "Job orders and their pipelines" },
          { to: "/candidates", label: "Candidates", description: "Everyone in your talent pool" },
          {
            to: "/search",
            label: "Find candidates",
            description: "Search by skills and meaning",
            show: on("candidate_search"),
          },
          {
            to: "/duplicates",
            label: "Duplicates",
            description: "Review likely duplicate candidates",
            count: pairs.data?.data.length ?? 0,
          },
          {
            to: "/skills",
            label: "Skills library",
            description: "The skills jobs and candidates are matched on",
          },
        ],
      },
    },
    {
      kind: "menu",
      group: {
        id: "clients",
        label: "Clients",
        items: [
          { to: "/companies", label: "Companies", description: "Client accounts and prospects" },
          {
            to: "/contacts",
            label: "Contacts",
            description: "The people at your clients, and where they've moved",
          },
          {
            to: "/agreements",
            label: "Agreements",
            description: "Each client's agreements, documents and key dates",
          },
          { to: "/placements", label: "Placements", description: "Consultants on assignment" },
          {
            to: "/extensions",
            label: "Extensions",
            description: "Placements ending soon, and whether they'll extend",
          },
          {
            to: "/vms",
            label: "VMS",
            description: "Client VMS programs and submittals",
            show: on("vms"),
          },
        ],
      },
    },
    {
      kind: "menu",
      group: {
        id: "back-office",
        label: "Back office",
        items: [
          {
            to: "/onboarding",
            label: "Onboarding",
            description: "Get placed consultants cleared to start",
          },
          { to: "/exports", label: "Time exports", description: "Approved time for invoicing" },
          {
            to: "/payroll",
            label: "Payroll",
            description: "Pay batches and off-cycle payments",
            show: on("payroll") && roles.some((r) => r === "admin" || r === "finance"),
          },
        ],
      },
    },
    { kind: "link", item: { to: "/reports", label: "Reports" } },
    {
      kind: "menu",
      group: {
        id: "ai",
        label: "AI",
        items: [
          {
            to: "/agents",
            label: "AI agents",
            description: "What each assistant costs, and how often it's accepted",
          },
          {
            to: "/agent-activity",
            label: "Agent activity",
            description: "Every AI action and the decision it led to",
          },
          {
            to: "/writing-style",
            label: "Writing style",
            description: "Style guides and suggested changes",
          },
          { to: "/templates", label: "Templates", description: "Message and document templates" },
          {
            to: "/sequences",
            label: "Sequences",
            description: "Multi-step outreach that stops on a reply",
            show: on("sequences"),
          },
        ],
      },
    },
  ];
  const adminMenu: NavEntry[] = [
    {
      kind: "menu",
      group: {
        id: "admin",
        label: "⚙ Admin",
        items: [
          { to: "/admin/team", label: "Team and roles", description: "Staff sign-ins and roles" },
          {
            to: "/admin/customization",
            label: "Customization",
            description: "Fields, pick lists and the change history",
          },
          {
            to: "/admin/templates",
            label: "Writing settings",
            description: "Personal styles, learning from edits, and who approves templates",
          },
          { to: "/import", label: "Import", description: "Bring in candidates and companies" },
          { to: "/privacy", label: "Privacy", description: "People's data requests" },
          { to: "/admin/api", label: "API", description: "Keys and webhooks for partner systems" },
          {
            to: "/admin/support",
            label: "Support and billing",
            description: "Help, support access and invoices",
          },
          {
            to: "/admin/demo",
            label: "Demo tenant",
            description: "Reset the demo to its starting state",
            show: Boolean(me.data?.tenant.demo),
          },
        ].map((i) => ({ ...i, show: admin && i.show !== false })),
      },
    },
  ];
  return (
    <div
      className="shell"
      style={
        brand?.primary_color
          ? ({ "--brand-primary": brand.primary_color } as React.CSSProperties)
          : undefined
      }
    >
      <nav className={brand?.primary_color ? "nav branded" : "nav"} aria-label="Main">
        <span className="brand">{PRODUCT_NAME}</span>
        {brand ? <FirmMark brand={brand} name={me.data?.tenant.name ?? ""} /> : null}
        {me.data?.tenant.demo ? (
          <span className="badge demo" title="Synthetic data only">
            Demo
          </span>
        ) : null}
        <NarrowMenu entries={[...entries, ...adminMenu]} />
        <NavEntries entries={entries} />
        <span className="spacer" />
        <span className="muted nav-user" title={me.data?.tenant.name}>
          {me.data ? `${me.data.display_name} · ${me.data.tenant.name}` : ""}
        </span>
        <NavEntries entries={adminMenu} align="right" />
        <button
          type="button"
          className="link"
          onClick={() => {
            auth.signOut();
            void navigate("/sign-in");
          }}
        >
          Sign out
        </button>
      </nav>
      <main>
        {status.data && !status.data.operational
          ? status.data.incidents
              .filter((i) => i.status !== "resolved")
              .map((i) => (
                <p key={i.id} className="notice" role="status">
                  StaffSmith status: {i.public_summary}
                </p>
              ))
          : null}
        <Outlet />
      </main>
    </div>
  );
}

export const routes: RouteObject[] = [
  { path: "/sign-in", element: <SignInPage /> },
  {
    path: "/",
    element: <Layout />,
    children: [
      { index: true, element: <Navigate to="/decisions" replace /> },
      { path: "decisions", lazy: page(() => import("./pages/Decisions.js")) },
      { path: "decisions/:decisionId", lazy: page(() => import("./pages/Decision.js")) },
      { path: "agent-activity", lazy: page(() => import("./pages/AgentActivity.js")) },
      { path: "admin/api", lazy: page(() => import("./pages/ApiAccess.js")) },
      { path: "admin/team", lazy: page(() => import("./pages/Team.js")) },
      { path: "admin/customization", lazy: page(() => import("./pages/Customization.js")) },
      { path: "admin/demo", lazy: page(() => import("./pages/DemoTenant.js")) },
      { path: "admin/templates", lazy: page(() => import("./pages/TemplatesAdmin.js")) },
      { path: "import", lazy: page(() => import("./pages/Import.js")) },
      { path: "templates", lazy: page(() => import("./pages/Templates.js")) },
      {
        path: "templates/:templateId",
        lazy: page(async () => ({ default: (await import("./pages/Templates.js")).TemplatePage })),
      },
      { path: "writing-style", lazy: page(() => import("./pages/WritingStyle.js")) },
      { path: "jobs", lazy: page(() => import("./pages/Jobs.js")) },
      { path: "jobs/:jobId", lazy: page(() => import("./pages/Job.js")) },
      { path: "candidates", lazy: page(() => import("./pages/Candidates.js")) },
      { path: "search", lazy: page(() => import("./pages/Search.js")) },
      { path: "duplicates", lazy: page(() => import("./pages/Duplicates.js")) },
      { path: "candidates/:candidateId", lazy: page(() => import("./pages/Candidate.js")) },
      { path: "companies", lazy: page(() => import("./pages/Companies.js")) },
      { path: "companies/:companyId", lazy: page(() => import("./pages/Company.js")) },
      { path: "contacts", lazy: page(() => import("./pages/Contacts.js")) },
      { path: "agreements", lazy: page(() => import("./pages/Agreements.js")) },
      { path: "agreements/:agreementId", lazy: page(() => import("./pages/Agreement.js")) },
      { path: "extensions", lazy: page(() => import("./pages/Extensions.js")) },
      { path: "skills", lazy: page(() => import("./pages/Skills.js")) },
      { path: "contacts/:contactId", lazy: page(() => import("./pages/Contact.js")) },
      { path: "agents", lazy: page(() => import("./pages/Agents.js")) },
      { path: "placements", lazy: page(() => import("./pages/Placements.js")) },
      { path: "placements/:placementId", lazy: page(() => import("./pages/Placement.js")) },
      { path: "onboarding", lazy: page(() => import("./pages/Onboarding.js")) },
      { path: "onboarding/:caseId", lazy: page(() => import("./pages/OnboardingCase.js")) },
      { path: "exports", lazy: page(() => import("./pages/Exports.js")) },
      { path: "payroll", lazy: page(() => import("./pages/Payroll.js")) },
      { path: "inbox", lazy: page(() => import("./pages/Inbox.js")) },
      {
        path: "inbox/:threadId",
        lazy: page(async () => ({ default: (await import("./pages/Inbox.js")).ThreadPage })),
      },
      { path: "sequences", lazy: page(() => import("./pages/Sequences.js")) },
      { path: "vms", lazy: page(() => import("./pages/Vms.js")) },
      { path: "privacy", lazy: page(() => import("./pages/Privacy.js")) },
      { path: "admin/support", lazy: page(() => import("./pages/SupportBilling.js")) },
      { path: "privacy/:requestId", lazy: page(() => import("./pages/PrivacyRequest.js")) },
      { path: "payroll/:batchId", lazy: page(() => import("./pages/PayrollBatch.js")) },
      { path: "reports", lazy: page(() => import("./pages/Reports.js")) },
    ],
  },
];
